import { z } from 'zod';

// One-directional: this module reads from lobby.js and lobby.js reads nothing
// back. An earlier arrangement had `boardTileViewSchema` here and the session
// snapshot importing it, which typechecked cleanly and then died at boot with
// "cannot access 'teamViewSchema' before initialization" -- a module cycle only
// shows up when the code actually runs.
import { teamViewSchema } from './lobby.js';

/**
 * Round and item shapes (§9.2, §9.3).
 *
 * The central rule of this file is the split between `PlayerItemView` and
 * anything holding an answer key. A stored `Item` has both `prompt` and
 * `answerKey`; what crosses to `/play` is this type, which has no field for a
 * key to hide in. Enforced by the serialiser in `apps/api` and by a test that
 * inspects every emitted payload (§9.5).
 */

/**
 * How long a verdict stays on a player's screen before the next question
 * replaces it.
 *
 * Shared with the server rather than kept as a client constant, because the
 * server budgets for it when stamping deadlines. D-6 prices a round at
 * N × (timer + reveal pause); a deadline schedule that left the pause out would
 * charge it to the student's next answer instead, and the last item of a round
 * would be several seconds shorter than the first.
 *
 * This is the floor, for an answer that was simply right. `revealMsFor` extends
 * it when there is something to read.
 */
export const ITEM_REVEAL_MS = 1200;

/**
 * The longest a verdict can hold the screen.
 *
 * The round's outer bound is budgeted against *this* rather than against the
 * pause actually taken, because the bound is stamped when the round starts and
 * nobody has answered anything yet. Budgeting the floor and then pausing longer
 * would quietly spend the last question's window.
 */
export const MAX_ITEM_REVEAL_MS = 6000;

/**
 * Extra seconds budgeted between questions on the round wall clock.
 *
 * Per-item answering time is unchanged; this is only the pause between items
 * that the *class* waits on, so one late phone cannot stretch the round by a
 * full reveal window per question.
 */
export const ROUND_TRANSITION_MS = 2000;

/**
 * How long to hold this verdict before drawing the next question.
 *
 * A tick and the word "Rétt!" is read in an instant, and lingering on it only
 * costs a student answering time. A six-blank conjugation grid with two wrong
 * is a table that has to be *read* -- which row was wrong, what was written,
 * what it should have been -- and 1.2 seconds is not enough to do that before
 * the screen changes. So the pause grows with how much there is to read, and
 * only for the rows that went wrong.
 *
 * One function, used by both sides: the client paces the transition with it and
 * the server adds the same number to the next item's deadline, so the extra
 * reading time is genuinely extra rather than taken out of the next answer.
 */
export function revealMsFor(result: { correct: boolean; breakdown: AnswerPart[] }): number {
  if (result.correct) return ITEM_REVEAL_MS;

  const wrong = result.breakdown.filter((part) => !part.correct).length;

  // A wrong single-answer question has one line to read, and lands at the base.
  // Each wrong row of a grid adds enough to find it and take it in.
  const needed = 1800 + 700 * Math.max(0, wrong - 1);

  return Math.min(needed, MAX_ITEM_REVEAL_MS);
}

/** What a player is shown for one item. Never contains the answer. */
export const itemDemoSchema = z.object({
  /** A sample prompt for the same exercise -- not the live question. */
  prompt: z.unknown(),
  /** Canonical sample answer, typed letter-by-letter in the demo UI. */
  sampleAnswer: z.string().min(1),
});

export type ItemDemo = z.infer<typeof itemDemoSchema>;

export const playerItemViewSchema = z.object({
  itemId: z.string().uuid(),
  /** 1-based, for "3 of 5". */
  ordinal: z.number().int().positive(),
  total: z.number().int().positive(),
  exerciseId: z.string(),
  /** Shape is exercise-specific; the renderer for `exerciseId` interprets it. */
  prompt: z.unknown(),
  /**
   * Absolute server timestamp, epoch milliseconds (§9.3).
   *
   * Absolute rather than a duration so a client can render a countdown that
   * stays honest across a slow frame or a backgrounded tab. Clients measure a
   * clock offset at handshake and count toward this; a wrong device clock
   * cannot buy a student extra seconds because `elapsedMs` is computed
   * server-side from receipt time regardless.
   *
   * Null while the round is paused (D-17).
   */
  deadlineAt: z.number().int().positive().nullable(),
  timeLimitMs: z.number().int().positive(),
  /**
   * Legacy optional sample for a first-question demo. No longer emitted;
   * kept optional so older clients still parse the payload.
   */
  demo: itemDemoSchema.nullable().optional(),
});

export type PlayerItemView = z.infer<typeof playerItemViewSchema>;

/**
 * One blank of an answer, written against expected.
 *
 * Structurally the `AnswerPart` that `@lpa/exercises` produces. Declared again
 * here rather than imported because this package is the wire contract and must
 * not depend on the generators; the API maps one to the other, and both are
 * three strings and a boolean.
 */
export const answerPartSchema = z.object({
  /** The blank's label ("þú", "þgf."), or null where there is one answer. */
  label: z.string().nullable(),
  /** What the student wrote. Empty if they left it blank or ran out of time. */
  given: z.string(),
  expected: z.string(),
  correct: z.boolean(),
  /** Credit for this part in [0, 1]. Near-miss accents are fractional. */
  partial: z.number().min(0).max(1).default(0),
  /** Near-miss note for this part, when fractional credit was awarded. */
  feedback: z.string().nullable().default(null),
});

export type AnswerPart = z.infer<typeof answerPartSchema>;

/** Feedback after a submission or a deadline. */
export const itemResultSchema = z.object({
  itemId: z.string().uuid(),
  correct: z.boolean(),
  /** 0..1; fractional where the exercise defines partial credit. */
  partial: z.number(),
  /**
   * Short Icelandic note for a near-miss typed answer. Grids usually leave
   * this empty and put notes on individual breakdown parts instead.
   */
  feedback: z.string().nullable().optional(),
  /**
   * The right answer, revealed only once the item is closed for this player.
   * This is the one place a key legitimately reaches a player, and it is
   * after their answer is recorded and can no longer change.
   */
  correctAnswer: z.unknown(),
  /**
   * Blank by blank: what they wrote, what was expected, and whether it counted.
   *
   * Added because a partially-right grid used to be reported to the student as
   * the word "Næstum því" and a pointer at the projector -- which told them
   * neither which blanks were wrong nor what the answer was, and put a personal
   * result on a screen the whole class was looking at. Partial credit is only
   * meaningful if you can see which parts earned it, and that belongs on the
   * device in the student's hand.
   */
  breakdown: z.array(answerPartSchema),
  /** True when the player ran out of time rather than answering. */
  timedOut: z.boolean(),
});

export type ItemResult = z.infer<typeof itemResultSchema>;

/** A team's round outcome, as shown to its own members and on the board. */
export const teamRoundOutcomeSchema = z.object({
  teamId: z.string().uuid(),
  exerciseId: z.string(),
  credit: z.number(),
  answered: z.number().int().nonnegative(),
  correct: z.number().int().nonnegative(),
  /** The credit denominator actually used, so a result can be explained. */
  members: z.number().int().nonnegative(),
  positionBefore: z.number().int().nonnegative(),
  positionAfter: z.number().int().nonnegative(),
  /** Tiles from the credit band (or stuck nudge), before any snake/ladder. */
  earnedMovement: z.number().int(),
  /** Extra tiles from a snake or ladder; 0 when none fired. */
  linkDelta: z.number().int(),
  movement: z.number().int(),
  /** The snake or ladder that fired on landing, if any (D-31). */
  linkLabel: z.string().nullable(),
  finished: z.boolean(),
  /**
   * True when this advance was forced after two consecutive stays on the same
   * exercise (credit too low to move both times). Snakes and ladders do not
   * apply to this step.
   */
  stuckNudge: z.boolean().optional(),
});

export type TeamRoundOutcome = z.infer<typeof teamRoundOutcomeSchema>;

/** One question as it is replayed back to the student who answered it. */
export const itemReviewSchema = z.object({
  itemId: z.string().uuid(),
  ordinal: z.number().int().positive(),
  /** The prompt as it was shown, so a question can be recognised again. */
  prompt: z.unknown(),
  correct: z.boolean(),
  partial: z.number(),
  /** True when the deadline passed with nothing submitted. */
  timedOut: z.boolean(),
  breakdown: z.array(answerPartSchema),
});

export type ItemReview = z.infer<typeof itemReviewSchema>;

/** What a player is told at round close (§9.3 `round:summary`). */
export const roundSummarySchema = z.object({
  roundOrdinal: z.number().int().positive(),
  credit: z.number(),
  /** Tiles from answers, before any snake/ladder. */
  earnedMovement: z.number().int(),
  /** Extra tiles from a snake or ladder; 0 when none. */
  linkDelta: z.number().int(),
  movement: z.number().int(),
  teamPosition: z.number().int().nonnegative(),
  linkLabel: z.string().nullable(),
  /**
   * This player's own five questions, right and wrong.
   *
   * A percentage with nothing behind it is a grade, not teaching: a student
   * told they got 80% has no way to find the one they missed. These are their
   * own answers only -- a team's result is shared, but who wrote what is not.
   */
  items: z.array(itemReviewSchema),
});

export type RoundSummary = z.infer<typeof roundSummarySchema>;

/** Throttled progress for the teacher (§9.2 `round:progress`). */
export const roundProgressSchema = z.object({
  teamId: z.string().uuid(),
  answered: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});

export type RoundProgress = z.infer<typeof roundProgressSchema>;

export const roundStartedSchema = z.object({
  roundId: z.string().uuid(),
  ordinal: z.number().int().positive(),
  itemsPerRound: z.number().int().positive(),
  /** Absolute server timestamp the round ends, epoch milliseconds. */
  closesAt: z.number().int().positive(),
  /** Which exercise each team drew, and from which tile (D-5, D-5a). */
  teamRounds: z.array(
    z.object({
      teamId: z.string().uuid(),
      exerciseId: z.string(),
      exerciseLabel: z.string(),
      tilePosition: z.number().int().nonnegative(),
    }),
  ),
});

export type RoundStarted = z.infer<typeof roundStartedSchema>;

/* ---------------------------------------------------------------
 * The teacher's per-question review (§9.2)
 * ------------------------------------------------------------- */

/**
 * Everything a teacher needs to answer "why was this marked wrong?".
 *
 * The question that prompted this is asked out loud, in front of the class,
 * within seconds of a round closing, and the teacher has to be able to answer
 * it from the screen already in front of them. So it is the whole round for one
 * team: every question, every student's answer to it, and the accepted answer
 * beside them.
 *
 * Fetched on demand rather than pushed with the round result. It is the only
 * payload in the game that carries per-student answers, it is several kilobytes
 * per team, and it is looked at for one team at a time -- usually not at all.
 */
export const teamRoundReviewSchema = z.object({
  teamId: z.string().uuid(),
  teamName: z.string(),
  exerciseId: z.string(),
  exerciseLabel: z.string(),
  items: z.array(
    z.object({
      itemId: z.string().uuid(),
      ordinal: z.number().int().positive(),
      prompt: z.unknown(),
      /** Every accepted answer, for a question that has more than one. */
      expected: z.array(z.string()),
      answers: z.array(
        z.object({
          playerId: z.string().uuid(),
          displayName: z.string(),
          correct: z.boolean(),
          partial: z.number(),
          /** True when the deadline passed with nothing submitted. */
          timedOut: z.boolean(),
          breakdown: z.array(answerPartSchema),
        }),
      ),
    }),
  ),
});

export type TeamRoundReview = z.infer<typeof teamRoundReviewSchema>;

export const roundReviewSchema = z.object({
  roundId: z.string().uuid(),
  roundOrdinal: z.number().int().positive(),
  teams: z.array(teamRoundReviewSchema),
});

export type RoundReview = z.infer<typeof roundReviewSchema>;

/**
 * Class-wide grammar/word mistakes for the Snákar og stigar projector.
 *
 * Built from the same stored responses as `roundReview`, but with student
 * names stripped, diacritic-only spelling slips hidden, and identical wrong
 * forms shown once. Multi-blank items walk one blank at a time.
 */
export const classMistakeBlankSchema = z.object({
  /** Case / person label, or null on a single-answer prompt. */
  label: z.string().nullable(),
  /** Canonical right form, shown in green at the top of the blank. */
  expected: z.string(),
  /** Unique wrong forms, as students wrote them. */
  mistakes: z.array(z.string()),
});

export type ClassMistakeBlank = z.infer<typeof classMistakeBlankSchema>;

export const classMistakePromptSchema = z.object({
  ordinal: z.number().int().positive(),
  prompt: z.string(),
  blanks: z.array(classMistakeBlankSchema),
});

export type ClassMistakePrompt = z.infer<typeof classMistakePromptSchema>;

export const classRoundMistakesSchema = z.object({
  exerciseLabel: z.string(),
  prompts: z.array(classMistakePromptSchema),
});

export type ClassRoundMistakes = z.infer<typeof classRoundMistakesSchema>;

/* ---------------------------------------------------------------
 * The board, as the teacher's screen renders it
 * ------------------------------------------------------------- */

export const boardMovedSchema = z.object({
  teamId: z.string().uuid(),
  from: z.number().int().nonnegative(),
  to: z.number().int().nonnegative(),
  linkLabel: z.string().nullable(),
  /** Explicit hop list when the path is not along the snakes track. */
  path: z.array(z.number().int().nonnegative()).optional(),
  /** Finndu fjögur: upgrade gained on this hop, if any. */
  collected: z.enum(['shoes', 'hammer', 'bow', 'clothes']).optional(),
  /** Finndu fjögur: why this token is walking, so the board can sequence kills. */
  kind: z.enum(['move', 'kill', 'home']).optional(),
  /** Finndu fjögur: pause on this hop's landing because it is a red bounce. */
  hazard: z.literal('red').optional(),
});

export type BoardMoved = z.infer<typeof boardMovedSchema>;

export const boardShotSchema = z.object({
  teamId: z.string(),
  from: z.number().int().nonnegative(),
  axis: z.enum(['up', 'down', 'left', 'right']),
  line: z.array(z.number().int().nonnegative()),
});

export type BoardShotPayload = z.infer<typeof boardShotSchema>;

/* ---------------------------------------------------------------
 * End of game (D-15). Aggregate only -- no per-student rows.
 * ------------------------------------------------------------- */

export const standingSchema = z.object({
  rank: z.number().int().positive(),
  team: teamViewSchema,
  finishRank: z.number().int().positive().nullable(),
});

export const sessionStatsSchema = z.object({
  roundsPlayed: z.number().int().nonnegative(),
  playerCount: z.number().int().nonnegative(),
  /** Mean credit across every response in the session. */
  classAccuracy: z.number(),
  /** Hardest first, which is what a teacher wants to reteach. */
  exerciseDifficulty: z.array(
    z.object({
      exerciseId: z.string(),
      exerciseLabel: z.string(),
      timesPlayed: z.number().int().nonnegative(),
      meanCredit: z.number(),
    }),
  ),
});

/**
 * One exercise a student should practise after the game.
 *
 * Built from their own answers only. `null` means every exercise they played
 * cleared the "good enough" bar (mean partial ≥ 0.7).
 */
export const practiceHintSchema = z
  .object({
    exerciseId: z.string(),
    exerciseLabel: z.string(),
    /** Their mean `partial` 0..1 across answers (and timeouts) for this exercise. */
    meanPartial: z.number(),
  })
  .nullable();

export type PracticeHint = z.infer<typeof practiceHintSchema>;

/**
 * Mean personal score at or above this is treated as fine — no practice tip.
 *
 * 0.7 sits in the five-tile band on the shared table (61–75% → 5; a clean
 * sweep moves eight): strong enough that the team is advancing well, weak
 * enough that a clear miss still gets a tip. Partial-credit grids make raw
 * "all correct" too harsh.
 */
export const PRACTICE_GOOD_MEAN = 0.7;

/**
 * Two or more teams reached the finish in the same close. The winner is the
 * higher whole-game percentage; equal percentages fall back to name order.
 */
export const finishTiebreakSchema = z.object({
  winnerName: z.string(),
  otherNames: z.array(z.string()).min(1),
  winnerMeanCredit: z.number(),
  runnerUpName: z.string(),
  runnerUpMeanCredit: z.number(),
});

export type FinishTiebreak = z.infer<typeof finishTiebreakSchema>;

export const sessionFinishedSchema = z.object({
  standings: z.array(standingSchema),
  stats: sessionStatsSchema,
  /**
   * Personal tip for the receiving player. Omitted on the teacher console;
   * present (possibly null) on `/play` when the session ends.
   */
  practiceHint: practiceHintSchema.optional(),
  /** Omitted unless two or more teams finished in the same round. */
  finishTiebreak: finishTiebreakSchema.optional(),
});

export type Standing = z.infer<typeof standingSchema>;
export type SessionStats = z.infer<typeof sessionStatsSchema>;
export type SessionFinished = z.infer<typeof sessionFinishedSchema>;

/* ---------------------------------------------------------------
 * Inbound
 * ------------------------------------------------------------- */

export const itemSubmitSchema = z.object({
  itemId: z.string().uuid(),
  /** Exercise-specific; parsed by the exercise's own responseSchema. */
  payload: z.unknown(),
});

export type ItemSubmit = z.infer<typeof itemSubmitSchema>;

export const itemReadySchema = z.object({
  itemId: z.string().uuid(),
});

export type ItemReady = z.infer<typeof itemReadySchema>;

/** Same payload as `item:ready`: which item the student started answering. */
export const itemEngagedSchema = itemReadySchema;

export type ItemEngaged = z.infer<typeof itemEngagedSchema>;

export const findFourMoveSchema = z.object({
  right: z.number().int().nonnegative().max(8).default(0),
  left: z.number().int().nonnegative().max(8).default(0),
  up: z.number().int().nonnegative().max(8).default(0),
  down: z.number().int().nonnegative().max(8).default(0),
});

export type FindFourMove = z.infer<typeof findFourMoveSchema>;

export const findFourBowSchema = z.object({
  shoot: z.boolean(),
  axis: z.enum(['up', 'down', 'left', 'right']).optional(),
});

export type FindFourBow = z.infer<typeof findFourBowSchema>;

/**
 * Everything a submission returns, in one acknowledgement.
 *
 * The next item travels in the ack rather than as a pushed `item:show`, and
 * that is not a style choice. Pushing it meant the new question and the verdict
 * for the previous one raced: the push arrived first, the ack resolved second
 * and overwrote it, and the student jumped straight past their feedback to the
 * next question.
 *
 * Carrying both lets the client hold the verdict on screen for a moment before
 * advancing -- the "reveal pause" D-6 already budgets for in its estimate of
 * round duration.
 */
export const submitResultSchema = z.object({
  result: itemResultSchema,
  /** Null when this was the player's last item for the round. */
  next: playerItemViewSchema.nullable(),
  answered: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});

export type SubmitResult = z.infer<typeof submitResultSchema>;
